import { Component } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * Class-based Error Boundary — must remain a class component, hooks-based
 * boundaries don't exist in React yet.
 *
 * Behaviour:
 *   • Catches render-time exceptions in the subtree and shows a friendly
 *     fallback card instead of unmounting the whole app to a white screen.
 *   • console.error logs full stack + componentStack for debugging.
 *   • "Reload Component" remounts only the boundary's children (via key bump);
 *     the surrounding chrome (sidebar, nav, toasts) stays intact.
 *   • Optional `resetKey` prop auto-resets the boundary when it changes — useful
 *     when wrapping module-switching containers so navigating away from a
 *     broken module clears the error automatically.
 *
 * Usage:
 *   <ErrorBoundary scope="Workforce" resetKey={activeTab}>
 *     <WorkforceModule />
 *   </ErrorBoundary>
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, resetCount: 0 };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Always log so a developer can find the stack trace in the console
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.scope ? ` · ${this.props.scope}` : ""}]`, error, info);
    this.setState({ info });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.handleReset();
    }
  }

  handleReset = () => {
    this.setState((s) => ({ error: null, info: null, resetCount: s.resetCount + 1 }));
  };

  render() {
    const { error, info, resetCount } = this.state;
    if (!error) {
      // Bump key on reset so children remount cleanly
      return (
        <div key={resetCount} style={{ display: "contents" }}>
          {this.props.children}
        </div>
      );
    }

    const scope = this.props.scope || "this section";
    const message = error?.message || String(error);

    return (
      <div className="p-4 sm:p-6" data-testid="error-boundary-fallback">
        <div className="max-w-2xl mx-auto bg-white border border-red-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="bg-red-50 border-b border-red-200 px-5 py-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-red-900">Something went wrong in {scope}</h3>
              <p className="text-xs text-red-700 mt-0.5">
                The rest of the app is still working. You can reload this section without losing your session.
              </p>
            </div>
          </div>

          <div className="px-5 py-4 space-y-3">
            <div className="bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs text-stone-700 font-mono break-words" data-testid="error-boundary-message">
              {message}
            </div>
            {info?.componentStack && (
              <details className="text-xs text-stone-500">
                <summary className="cursor-pointer font-semibold hover:text-stone-700">Technical details</summary>
                <pre className="mt-2 max-h-64 overflow-auto bg-stone-50 border border-stone-200 rounded p-2 whitespace-pre-wrap">
                  {info.componentStack.trim()}
                </pre>
              </details>
            )}
          </div>

          <div className="bg-stone-50 border-t border-stone-200 px-5 py-3 flex flex-wrap items-center justify-end gap-2">
            {this.props.onHome && (
              <button
                data-testid="error-boundary-home-btn"
                onClick={this.props.onHome}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-stone-700 bg-white border border-stone-300 hover:bg-stone-50 min-h-[40px]"
              >
                <Home className="w-4 h-4" /> Go home
              </button>
            )}
            <button
              data-testid="error-boundary-reload-btn"
              onClick={this.handleReset}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-forest-700 hover:bg-forest-800 min-h-[40px]"
            >
              <RefreshCw className="w-4 h-4" /> Reload component
            </button>
          </div>
        </div>
      </div>
    );
  }
}
